import { useTranslation } from 'react-i18next';
import type { FinanceHeroSnapshot } from '@/application/metrics';
import { formatCurrency } from '@/presentation/components/metrics/metricFormatters';
import { formatSigned } from '@/presentation/utils/metricDeltas';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';
import { MetricDefinitionTooltip } from '@/presentation/components/metrics/MetricDefinitionTooltip';

const ANOMALY_STYLES: Record<
  FinanceHeroSnapshot['anomaly'],
  { shell: string; badge: string }
> = {
  none: {
    shell: 'metric-hero-banner--ok',
    badge: 'ds-badge ds-badge--neutral',
  },
  watch: {
    shell: 'metric-hero-banner--watch',
    badge: 'ds-badge ds-badge--watch',
  },
  critical: {
    shell: 'metric-hero-banner--critical',
    badge: 'ds-badge ds-badge--critical',
  },
};

interface FinanceHeroStripProps {
  snapshot: FinanceHeroSnapshot | null;
  locale: string;
  loading?: boolean;
  error?: string | null;
  /** Sin permiso metrics.caja: no mostrar números sensibles. */
  forbidden?: boolean;
}

function anomalyReasonText(snapshot: FinanceHeroSnapshot, locale: string, t: (key: string, opts?: Record<string, string>) => string): string {
  const reason = snapshot.anomalyPrimaryReason;
  if (reason === 'none') {
    return t('domain.finances.hero.anomalyReason.none');
  }

  const revenuePct =
    snapshot.revenueDeltaPct === null
      ? '—'
      : formatSigned(locale, snapshot.revenueDeltaPct);
  const netPct =
    snapshot.vsPreviousPct === null ? '—' : formatSigned(locale, snapshot.vsPreviousPct);

  if (reason === 'payment_mix' && snapshot.anomalyMix) {
    const pp = formatSigned(locale, snapshot.anomalyMix.shareDeltaPp, 0);
    return t('domain.finances.hero.anomalyReason.payment_mix', {
      method: snapshot.anomalyMix.methodLabel ?? t('domain.finances.hero.topMethodUnassigned'),
      share: snapshot.anomalyMix.sharePct.toLocaleString(locale, { maximumFractionDigits: 0 }),
      pp,
    });
  }

  return t(`domain.finances.hero.anomalyReason.${reason}`, {
    pct: reason.startsWith('net') ? netPct : revenuePct,
  });
}

/** Franja fija de Finanzas: neto (hero) · contexto primary · detalle compacto. */
export function FinanceHeroStrip({
  snapshot,
  locale,
  loading = false,
  error = null,
  forbidden = false,
}: FinanceHeroStripProps) {
  const { t } = useTranslation();

  if (forbidden) {
    return (
      <section
        data-testid="finance-hero-strip"
        aria-label={t('domain.finances.hero.ariaLabel')}
        className="metric-hero-banner metric-hero-banner--ok"
      >
        <p className="panel-body text-sm">{t('domain.finances.hero.forbidden')}</p>
      </section>
    );
  }

  if (loading && !snapshot) {
    return (
      <section
        data-testid="finance-hero-strip"
        aria-busy="true"
        aria-label={t('domain.finances.hero.ariaLabel')}
        className="metric-hero-banner metric-hero-banner--ok"
      >
        <LoadingBlock minHeightClassName="min-h-[5.5rem]" label={t('domain.finances.hero.loading')} />
      </section>
    );
  }

  if (error && !snapshot) {
    return (
      <section
        data-testid="finance-hero-strip"
        role="alert"
        aria-label={t('domain.finances.hero.ariaLabel')}
        className="metric-hero-banner metric-hero-banner--error"
      >
        <p className="ds-tone-critical text-sm">{error}</p>
      </section>
    );
  }

  if (!snapshot) {
    return null;
  }

  const styles = ANOMALY_STYLES[snapshot.anomaly];
  const scopeLabel =
    snapshot.scope === 'network'
      ? t('domain.finances.hero.scopeNetwork')
      : t('domain.finances.hero.scopeClinic');
  const vsLabel =
    snapshot.vsPreviousPct === null
      ? t('domain.finances.hero.vsNoCompare')
      : t('domain.finances.hero.vsValue', {
          pct: formatSigned(locale, snapshot.vsPreviousPct),
        });
  const topLabel = snapshot.topMethod
    ? t('domain.finances.hero.topMethodValue', {
        method: snapshot.topMethod.label ?? t('domain.finances.hero.topMethodUnassigned'),
        share: snapshot.topMethod.sharePct.toLocaleString(locale, { maximumFractionDigits: 0 }),
      })
    : snapshot.scope === 'network'
      ? t('domain.finances.hero.topMethodNetwork')
      : t('domain.finances.hero.topMethodEmpty');
  const reasonText = anomalyReasonText(snapshot, locale, t as (key: string, opts?: Record<string, string>) => string);

  return (
    <section
      data-testid="finance-hero-strip"
      data-anomaly={snapshot.anomaly}
      data-anomaly-reason={snapshot.anomalyPrimaryReason}
      aria-label={t('domain.finances.hero.ariaLabel')}
      className={`metric-hero-banner ${styles.shell}`}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="panel-section-title tracking-[0.08em]">
          {scopeLabel}
        </span>
        {snapshot.empty ? (
          <span className="panel-muted text-xs">
            {t('domain.finances.hero.emptyMonth')}
          </span>
        ) : null}
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
        <div data-testid="finance-hero-neto" className="min-w-0 shrink-0 lg:min-w-[12rem] lg:flex-1">
          <p className="metric-kpi__label flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.1em] sm:text-sm">
            <span>{t('domain.finances.hero.net')}</span>
            <MetricDefinitionTooltip metricId="net" size="sm" align="start" />
          </p>
          <p className="metric-kpi__value bi-num mt-1 text-3xl font-bold tracking-tight sm:text-4xl">
            {formatCurrency(snapshot.neto, locale)}
          </p>
        </div>

        <div className="metric-primary-strip grid-cols-2 sm:grid-cols-3 lg:flex-1 xl:grid-cols-3">
          <div data-testid="finance-hero-vs" className="metric-kpi metric-kpi--primary min-w-0">
            <p className="metric-kpi__label">{t('domain.finances.hero.vsPrevious')}</p>
            <p className="metric-kpi__value bi-num">{vsLabel}</p>
          </div>

          <div data-testid="finance-hero-method" className="metric-kpi metric-kpi--primary min-w-0">
            <p className="metric-kpi__label flex items-center gap-1">
              <span className="truncate">{t('domain.finances.hero.topMethod')}</span>
              <MetricDefinitionTooltip metricId="topPaymentMethod" size="sm" align="end" />
            </p>
            <p className="metric-kpi__value bi-num text-lg sm:text-xl">{topLabel}</p>
          </div>

          <div data-testid="finance-hero-anomaly" className="metric-kpi metric-kpi--primary min-w-0 sm:col-span-2 lg:col-span-1">
            <p className="metric-kpi__label">{t('domain.finances.hero.anomaly')}</p>
            <p className="mt-1">
              <span
                className={styles.badge}
              >
                {t(`domain.finances.hero.anomalyLevel.${snapshot.anomaly}`)}
              </span>
            </p>
            <p
              data-testid="finance-hero-anomaly-reason"
              className="metric-kpi__detail mt-1 leading-snug"
            >
              {reasonText}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
